import React, { useState } from 'react';
import '../styles/PetAdoptionFeedback.css';

interface FeedbackItem {
  id: number;
  userName: string;
  userAvatar: string;
  dogName: string;
  dogBreed: string;
  dogImage: string;
  rating: number;
  feedback: string;
  adoptionDate: string;
  location: string;
  dogAge?: number;
  dogGender?: string;
  favoriteActivity?: string;
  detailedStory?: string;
  beforeAdoption?: string;
}

interface PetAdoptionFeedbackProps {
  summaryMode?: boolean;
}

const PetAdoptionFeedback: React.FC<PetAdoptionFeedbackProps> = ({ summaryMode = false }) => {
  const [expandedCard, setExpandedCard] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;
  
  const feedbackData: FeedbackItem[] = [
    {
      id: 1,
      userName: "Sarah Johnson",
      userAvatar: "https://randomuser.me/api/portraits/women/68.jpg",
      dogName: "Max",
      dogBreed: "Golden Retriever",
      dogImage: "/images/set6.jpeg",
      rating: 5,
      feedback: "The adoption process at HolyDogs was incredibly smooth! Max has brought so much joy to our family.",
      adoptionDate: "2023-06-15",
      location: "New York, NY",
      dogAge: 3,
      dogGender: "Male",
      favoriteActivity: "Playing fetch at the park",
      detailedStory: "Max came to us after being rescued from a hoarding situation. When we first met him, he was shy and hesitant, but with patience and love, he's blossomed into the most confident and affectionate companion. He now enjoys weekend hikes and has even become a therapy dog at our local children's hospital.",
      beforeAdoption: "Rescued from overcrowded shelter"
    },
    {
      id: 2,
      userName: "Michael Chen",
      userAvatar: "https://randomuser.me/api/portraits/men/41.jpg",
      dogName: "Luna",
      dogBreed: "Labrador Retriever",
      dogImage: "/images/set1.jpeg",
      rating: 5,
      feedback: "Luna is the perfect addition to our family! The HolyDogs team helped us find exactly what we were looking for.",
      adoptionDate: "2023-07-22",
      location: "San Francisco, CA",
      dogAge: 2,
      dogGender: "Female",
      favoriteActivity: "Swimming at the beach",
      detailedStory: "Luna was found as a stray with an injured leg. After surgery and rehabilitation at HolyDogs, she made a full recovery. She's now the star of our neighborhood dog park and loves playing with other dogs. Her favorite thing is when we take her to the ocean - she'll swim for hours!",
      beforeAdoption: "Rescued stray with leg injury"
    },
    {
      id: 3,
      userName: "Emma Rodriguez",
      userAvatar: "https://randomuser.me/api/portraits/women/32.jpg",
      dogName: "Charlie",
      dogBreed: "French Bulldog",
      dogImage: "/images/set2.jpeg",
      rating: 5,
      feedback: "Charlie is absolutely wonderful! The adoption counselor at HolyDogs took the time to educate us about his specific needs.",
      adoptionDate: "2023-05-10",
      location: "Miami, FL",
      dogAge: 4,
      dogGender: "Male",
      favoriteActivity: "Snuggling on the couch",
      detailedStory: "Charlie was surrendered when his previous owners moved overseas. He had some separation anxiety at first, but with consistent training and a stable routine, he's become the most loving companion. He's particularly fond of our morning walks and afternoon naps. His quirky personality keeps us laughing every day!",
      beforeAdoption: "Surrendered by previous owners"
    },
    {
      id: 4,
      userName: "David Wilson",
      userAvatar: "https://randomuser.me/api/portraits/men/62.jpg",
      dogName: "Bella",
      dogBreed: "Beagle",
      dogImage: "/images/set4.jpeg",
      rating: 5,
      feedback: "Bella has transformed our lives in the best way possible. She's so loving and well-behaved.",
      adoptionDate: "2023-08-05",
      location: "Chicago, IL",
      dogAge: 5,
      dogGender: "Female",
      favoriteActivity: "Tracking scents in the woods",
      detailedStory: "Bella was rescued from a puppy mill where she spent years as a breeding dog. When we adopted her, she was timid and unsure of human touch. With gentle encouragement and positive reinforcement, she's learned to trust again. She now has the most joyful howl and loves exploring nature trails with us on weekends.",
      beforeAdoption: "Rescued from puppy mill"
    },
    {
      id: 5,
      userName: "Olivia Parker",
      userAvatar: "https://randomuser.me/api/portraits/women/83.jpg",
      dogName: "Rocky",
      dogBreed: "German Shepherd",
      dogImage: "/images/set3.jpeg",
      rating: 5,
      feedback: "Rocky has been an amazing addition to our family! HolyDogs made sure we were fully prepared for a large breed dog.",
      adoptionDate: "2023-07-30",
      location: "Seattle, WA",
      dogAge: 2,
      dogGender: "Male",
      favoriteActivity: "Playing frisbee in the backyard",
      detailedStory: "Rocky was a police dog candidate who didn't quite make the cut due to his overly friendly nature. He's found his true calling as a family protector and playmate. He's incredibly intelligent and has learned over 20 commands. We're currently training him for search and rescue work - he has an amazing nose!",
      beforeAdoption: "Police K9 program dropout"
    },
    {
      id: 6,
      userName: "Jane Thompson",
      userAvatar: "https://randomuser.me/api/portraits/women/34.jpg",
      dogName: "Daisy",
      dogBreed: "Poodle",
      dogImage: "/images/set5.jpeg",
      rating: 5,
      feedback: "Adopted Daisy for my elderly mother, and she's been the perfect companion! HolyDogs matched us with a gentle, well-trained dog.",
      adoptionDate: "2023-06-28",
      location: "Austin, TX",
      dogAge: 7,
      dogGender: "Female",
      favoriteActivity: "Gentle walks around the neighborhood",
      detailedStory: "Daisy was found wandering in a rural area, matted and underweight. After being groomed and nursed back to health at HolyDogs, she became the perfect companion for my 82-year-old mother. Daisy is incredibly gentle and intuitive - she even alerts us when my mother's blood sugar drops. Their bond is truly special.",
      beforeAdoption: "Found as a stray in poor condition"
    },
    {
      id: 7,
      userName: "Robert Kim",
      userAvatar: "https://randomuser.me/api/portraits/men/22.jpg",
      dogName: "Oscar",
      dogBreed: "Dachshund",
      dogImage: "/images/set7.jpeg",
      rating: 5,
      feedback: "Oscar has brought so much laughter to our home! His personality is bigger than he is.",
      adoptionDate: "2023-09-12",
      location: "Portland, OR",
      dogAge: 4,
      dogGender: "Male",
      favoriteActivity: "Burrowing under blankets",
      detailedStory: "Oscar came from a home with too many dogs and not enough attention. He was shy at first but has blossomed into the most hilarious little character. He has a collection of sweaters for every season and loves 'helping' with chores by following us around. His favorite game is hiding toys in unexpected places!",
      beforeAdoption: "From overcrowded home"
    },
    {
      id: 8,
      userName: "Lisa Anderson",
      userAvatar: "https://randomuser.me/api/portraits/women/45.jpg",
      dogName: "Molly",
      dogBreed: "Border Collie",
      dogImage: "/images/set8.jpeg",
      rating: 5,
      feedback: "Molly's intelligence is astonishing! HolyDogs matched us perfectly with our active lifestyle.",
      adoptionDate: "2023-08-18",
      location: "Denver, CO",
      dogAge: 3,
      dogGender: "Female",
      favoriteActivity: "Agility training courses",
      detailedStory: "Molly was surrendered because her previous owners couldn't meet her high energy needs. As avid hikers and runners, we were the perfect match! She accompanies us on all our adventures and has learned to carry her own supplies in a dog backpack. We've even started competing in agility competitions together.",
      beforeAdoption: "Surrendered for being 'too energetic'"
    },
    {
      id: 9,
      userName: "Thomas Wright",
      userAvatar: "https://randomuser.me/api/portraits/men/75.jpg",
      dogName: "Zoe",
      dogBreed: "Siberian Husky",
      dogImage: "/images/set9.jpeg",
      rating: 5,
      feedback: "Zoe's beautiful blue eyes captured our hearts the moment we saw her. The adoption process was seamless.",
      adoptionDate: "2023-07-05",
      location: "Minneapolis, MN",
      dogAge: 1,
      dogGender: "Female",
      favoriteActivity: "Pulling a sled in winter",
      detailedStory: "Zoe was part of an accidental litter when her previous owners didn't spay their pet. She's full of youthful energy and loves the snow - we've even built her a mini obstacle course in our backyard! Her most endearing habit is 'talking' to us with a range of howls and woofs when she wants attention.",
      beforeAdoption: "From accidental litter"
    }
  ];

  // Pagination logic
  const totalPages = Math.ceil(feedbackData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  
  // Show only 3 items in summary mode, paginated items in full mode
  const displayedData = summaryMode 
    ? feedbackData.slice(0, 3) 
    : feedbackData.slice(startIndex, endIndex);

  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('');
  };

  const renderStars = (rating: number) => {
    return (
      <div className="stars">
        {[...Array(5)].map((_, i) => (
          <span 
            key={i} 
            className={`star ${i < rating ? 'filled' : ''}`}
          >
            ★
          </span>
        ))}
      </div>
    );
  };

  const toggleExpand = (id: number) => {
    setExpandedCard(expandedCard === id ? null : id);
  };

  const nextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const goToPage = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Render page numbers
  const renderPageNumbers = () => {
    const pages = [];
    const maxPagesToShow = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    const endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
    
    if (endPage - startPage + 1 < maxPagesToShow) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          className={`page-number ${currentPage === i ? 'active' : ''}`}
          onClick={() => goToPage(i)}
        >
          {i}
        </button>
      );
    }
    
    return pages;
  };

  return (
    <div className={`feedback-section ${summaryMode ? 'summary-mode' : ''}`}><br/><br/><br/>
      <div className="section-header">
        <h2>HolyDogs Happy Tails</h2>
        <p>Heartwarming stories from families who found their perfect furry companions</p>
        {!summaryMode && (
          <div className="stats-container">
            <div className="stat-item">
              <div className="stat-number">9,500+</div>
              <div className="stat-number">Successful Adoptions</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">98%</div>
              <div className="stat-number">Happy Families</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">24/7</div>
              <div className="stat-number">Support Available</div>
            </div>
          </div>
        )}
      </div>
      
      <div className="feedback-grid">
        {displayedData.map(item => (
          <div 
            key={item.id} 
            className={`feedback-card ${expandedCard === item.id ? 'expanded' : ''}`}
          >
            <div className="card-header">
              <div className="user-info">
                <div className="avatar">
                  {item.userAvatar ? (
                    <img src={item.userAvatar} alt={item.userName} />
                  ) : (
                    <span>{getInitials(item.userName)}</span>
                  )}
                </div>
                <div className="user-details">
                  <h3>{item.userName}</h3>
                  <p className="location">{item.location}</p>
                </div>
              </div>
              <div className="adoption-date">
                Adopted {new Date(item.adoptionDate).toLocaleDateString('en-US', { 
                  month: 'short', 
                  day: 'numeric', 
                  year: 'numeric' 
                })}
              </div>
            </div>
            
            <div className="dog-image-container">
              <img src={item.dogImage} alt={item.dogName} className="dog-image" />
              <div className="dog-overlay">
                <h4 className="dog-name">{item.dogName}</h4>
                <p className="dog-breed">{item.dogBreed}</p>
                {!summaryMode && (
                  <div className="dog-details">
                    <span>{item.dogAge} years old</span>
                    <span>{item.dogGender}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="rating-section">
              {renderStars(item.rating)}
              <span className="rating-text">
                {item.rating} out of 5 stars
              </span>
            </div>
            
            <div className="feedback-text">
              <p>{
                summaryMode 
                  ? `"${item.feedback.substring(0, 100)}${item.feedback.length > 100 ? '...' : ''}"`
                  : `"${item.feedback}"`
              }</p>
              
              {!summaryMode && item.favoriteActivity && (
                <div className="favorite-activity">
                  <span>Favorite Activity: </span>
                  {item.favoriteActivity}
                </div>
              )}
              
              {!summaryMode && item.beforeAdoption && (
                <div className="before-adoption">
                  <span>Before HolyDogs: </span>
                  {item.beforeAdoption}
                </div>
              )}
              
              {!summaryMode && expandedCard === item.id && item.detailedStory && (
                <div className="detailed-story">
                  <p>{item.detailedStory}</p>
                </div>
              )}
            </div>
            
            <div className="holy-paw">
              <div className="paw-icon">🐾</div>
              <span>HolyDogs Adoption</span>
              
              {!summaryMode && item.detailedStory && (
                <button 
                  className="read-more-btn"
                  onClick={() => toggleExpand(item.id)}
                >
                  {expandedCard === item.id ? 'Show Less' : 'Read Full Story'}
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
      
      {!summaryMode && (
        <div className="section-footer">
          <div className="pagination-controls">
            <button 
              className={`pagination-button ${currentPage === 1 ? 'disabled' : ''}`}
              onClick={prevPage}
              disabled={currentPage === 1}
            >
              Previous
            </button>
            
            <div className="page-numbers">
              {renderPageNumbers()}
            </div>
            
            <button 
              className={`pagination-button ${currentPage === totalPages ? 'disabled' : ''}`}
              onClick={nextPage}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </div>
          
          <div className="testimonial-quote">
            <p>"HolyDogs didn't just help us adopt a pet - they helped us complete our family. 
            The ongoing support and community they provide is invaluable."</p>
            <div className="quote-author">- The Peterson Family</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PetAdoptionFeedback;